import { emailConfirm, emailGenerateCode } from '@frontend/data-access';
import { Alert, AlertMessage } from '@frontend/shared/ui';
import { Button, Col, Form, Input, Row, Spin, Typography } from 'antd';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useCallback, useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { getAuthErrorMessages } from '../auth-helper';

/* eslint-disable-next-line */
export interface ConfirmProps {}

export function Confirm(props: ConfirmProps) {
  const { t } = useTranslation('common');
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [resendClicked, setResendClicked] = useState(false);
  const [alert, setAlert] = useState<Alert>();
  const router = useRouter();
  const token = router.query['confirmation_token'];

  const handleConfirm = useCallback(async () => {
    if (router?.query['confirmation_token']) {
      try {
        setAlert(undefined);
        setLoading(true);
        const response = await emailConfirm({
          token: router?.query['confirmation_token']?.toString(),
        });
        if (response.status === 200) {
          setAlert({
            messages: [t('profile_verified')],
            type: 'success',
          });
        } else {
          setAlert({
            messages: getAuthErrorMessages(['auth.invalid_token']),
            type: 'error',
          });
        }
      } catch (error) {
        let message = t('unknown_error');
        if (error instanceof Error) message = error.message;
        setAlert({ messages: [message], type: 'error' });
      } finally {
        setLoading(false);
      }
    }
  }, [router]);

  useEffect(() => {
    if (token) {
      handleConfirm();
    }
  }, [token, handleConfirm]);

  const handleResend = async (values: { email: string }) => {
    try {
      setAlert(undefined);
      setLoading(true);

      if (!executeRecaptcha) {
        console.log(t('recaptcha_not_available'));
        return;
      }
      const token = await executeRecaptcha('email_verification');

      const { status } = await emailGenerateCode({
        email: values.email,
        captcha: token,
      });

      if (status === 200) {
        setAlert({
          messages: [t('email_sent')],
          type: 'success',
        });
      } else {
        setAlert({ messages: [t('token_expired')], type: 'error' });
      }
    } catch (error) {
      let message = t('email_verification_error');
      if (error instanceof Error) message = error.message;
      setAlert({ messages: [message], type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Col className="w-96">
      <Col className="flex justify-center">
        <Typography.Title level={3}>Join Ideation Bridge</Typography.Title>
      </Col>
      {alert && <AlertMessage className="my-6" alert={alert} />}
      {alert?.type === 'error' && (
        <Row className="py-4 flex justify-center">
          {resendClicked ? (
            <Form
              layout="vertical"
              form={form}
              onFinish={handleResend}
              className="w-full"
            >
              <Form.Item
                label={t('email')}
                name="email"
                rules={[
                  {
                    required: true,
                    type: 'email',
                    message: t('input_email'),
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item>
                <Button
                  loading={loading}
                  block
                  size="large"
                  type="primary"
                  shape="round"
                  htmlType="submit"
                >
                  {t('resend')}
                </Button>
              </Form.Item>
            </Form>
          ) : (
            <Typography>
              <Button
                loading={loading}
                onClick={() => setResendClicked(true)}
                type="link"
              >
                {t('resend_activation_link')}
              </Button>
            </Typography>
          )}
        </Row>
      )}
      {loading && (
        <Col className="flex justify-center">
          <Spin />
        </Col>
      )}
      <Row className="py-4 flex justify-center">
        <Typography>
          <Link href="login">{t('back_to_login')}</Link>
        </Typography>
      </Row>
    </Col>
  );
}

export default Confirm;
